import { useQuery } from '@tanstack/react-query'
import { graphqlClient } from '@/core/api/graphql-client'
import {
  UserNoticeDeadEndDocument,
  UserNoticeDocument,
  type UserNoticeSurface,
} from '@/generated/graphql'

// The server counts a positive verdict as today's delivery, so a SUCCESSFUL answer must
// not be re-fetched: one ask per session, no window-focus refetch, never stale.
//
// A FAILED ask is different — it records nothing, and the day's show is claimed per
// (user, surface), so a retry can only ever be answered once. Without a retry a single
// blip would suppress the notice until a full page reload.
function userNoticeQueryOptions(surface: UserNoticeSurface) {
  return {
    queryKey: ['user-notice', surface] as const,
    queryFn: () => graphqlClient.request(UserNoticeDocument, { surface }),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 1,
  }
}

export function useUserNotice(enabled: boolean, surface: UserNoticeSurface) {
  return useQuery({ ...userNoticeQueryOptions(surface), enabled })
}

// Reads a verdict already in the cache without asking for one. Asking is what spends the
// day's show, so a surface that must not ask can still honour a dead-end that a surface
// which did ask has already paid for.
export function useCachedUserNotice(surface: UserNoticeSurface) {
  return useQuery({ ...userNoticeQueryOptions(surface), enabled: false })
}

// The dead-end's OWN ask. `peek` records no delivery, so unlike the query above this one may
// run anywhere — which is what covers the case no cache read can: a cold load straight into a
// route the nudge never renders on.
//
// Its own cache entry, deliberately: a peek answers NONE where a real ask would answer SOFT,
// so sharing one would let it erase a nudge the gate was about to render. Fixed LAUNCH, since
// the server keeps the dead-end out of the PROFILE surface.
export function useUserNoticePeek(enabled: boolean) {
  return useQuery({
    queryKey: ['user-notice-peek'] as const,
    queryFn: () => graphqlClient.request(UserNoticeDeadEndDocument),
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 1,
  })
}
